// react
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

// components
import { Button, Container, ProgressBar, Title } from '@/components/atoms';

/**
 * CareProfileHeader shows the current progress of the care plan.
 */
export const CareProfileHeader = ({
  className,
  limit,
  progress,
  toBack,
  toBackLabel,
  title,
  onPrintPdf,
  name,
  onOpenSections,
}: CareProfileHeaderProps) => {
  const headerTitle = useMemo(() => {
    return title || `${name || 'Your Loved One'}'s Care Assessment`;
  }, [name, title]);

  return (
    <Wrapper className={className}>
      <Container size='md'>
        <Body>
          <BackRow>
            <Button
              ariaLabel='back'
              variant='secondary'
              iconLeft={'chevron-left'}
              to={toBack}
              size='sm'
            >
              {toBackLabel}
            </Button>
            {onPrintPdf && (
              <PrintWrapper>
                <Button
                  ariaLabel='print'
                  variant='link'
                  onPress={onPrintPdf}
                  iconLeft={'print'}
                  size='md'
                >
                  Print
                </Button>
              </PrintWrapper>
            )}
            <Button
              ariaLabel='sections'
              variant='link'
              iconLeft='list'
              onPress={onOpenSections}
              size='sm'
            >
              Sections
            </Button>
          </BackRow>
          <Content>
            <StyledTitle>{headerTitle}</StyledTitle>
            <StyledProgressBar
              label='Progress'
              limit={limit || 1}
              progress={progress}
            />
          </Content>
        </Body>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  background: ${(props) => props.theme.color.neutral[10]};
  padding: ${(props) => props.theme.spacing[24]} 0;
  border-bottom: 1px solid ${(props) => props.theme.color.neutral[30]};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
`;

const BackRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.theme.spacing[12]};
`;

const StyledProgressBar = styled(ProgressBar)`
  width: 100%;
`;

const PrintWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-right: ${(props) => props.theme.spacing[16]};
`;

const StyledTitle = styled(Title)`
  margin: 0;
  text-align: center;
  font: ${(props) => props.theme.typography.heading['3xl']};
  ${(props) =>
    props.theme.breakpoint.sm(css`
      font: ${props.theme.typography.heading['4xl']};
    `)}
`;

interface CareProfileHeaderProps {
  className?: string;
  limit?: number;
  progress?: number;
  toBack?: string;
  toBackLabel?: string;
  title?: string;
  onPrintPdf?: () => void;
  name?: string;
  onOpenSections: () => void;
}

CareProfileHeader.displayName = 'CareProfileHeader';

export default CareProfileHeader;
